import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export default function TrainingSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const { totalDuration, drillCount, newStreak, milestoneReached, pendingSync, sessionShurikens, newWeeklyShurikens, newRank } = location.state || {};
  const [confetti, setConfetti] = useState([]);
  
  // Generate confetti for first day milestone
  useEffect(() => {
    if (milestoneReached === 1) {
      const colors = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a8e6cf', '#ff8b94', '#95e1d3'];
      const particles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 4, // Random size between 4-8px
      }));
      setConfetti(particles);
      
      // Clear confetti after animation
      const timer = setTimeout(() => setConfetti([]), 5000);
      return () => clearTimeout(timer);
    }
  }, [milestoneReached]);
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t('training.minutes')}`;
  };
  
  // Get the appropriate title based on count
  const getMilestoneTitle = () => {
    if (!milestoneReached) return t('success.great_work');
    if (milestoneReached === 1) return t('success.milestone_title_first_day');
    return t('success.milestone_title');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Confetti animation for first day */}
      {milestoneReached === 1 && confetti.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {confetti.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full opacity-75"
              style={{
                left: `${particle.left}%`,
                top: '-20px',
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
                animation: `confetti-fall ${particle.duration}s ${particle.delay}s ease-in forwards`,
              }}
            />
          ))}
        </div>
      )}
      
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 md:p-12 text-center relative z-10">
        {!milestoneReached && (
          <video 
            src="/celebrate/ninja-cut-bamboo.mp4" 
            autoPlay
            loop
            muted
            playsInline
            className="mx-auto mb-4 md:mb-6 max-w-[67.5%] h-auto rounded-full animate-video-celebration"
          />
        )}
        <h1 className="text-3xl md:text-6xl font-bold mb-6 md:mb-12">
          {milestoneReached && milestoneReached > 1 ? '🔥 ' : ''}
          {getMilestoneTitle()}
        </h1>
        
        {milestoneReached ? (
          <div className="mb-6 md:mb-12">
            {milestoneReached === 1 ? (
              <p className="text-2xl md:text-4xl mb-6 md:mb-10">
                {t('success.first_day_message')}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl mb-6 md:mb-10">
                {t('success.milestone_message_other', { count: milestoneReached })}
              </p>
            )}
            <p className="text-xl md:text-3xl">{t('success.keep_going')}</p>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-10 mb-6 md:mb-12">
            <div>
              <div className="text-gray-600 text-base md:text-2xl">{t('success.total_training')}</div>
              <div className="text-3xl md:text-5xl font-bold">{formatDuration(totalDuration)}</div>
            </div>
            
            <div>
              <div className="text-gray-600 text-base md:text-2xl">{t('success.drills_completed')}</div>
              <div className="text-3xl md:text-5xl font-bold">{drillCount}</div>
            </div>
            
            <div>
              <div className="text-gray-600 text-base md:text-2xl">{t('success.streak')}</div>
              <div className="text-3xl md:text-5xl font-bold">🔥 {newStreak} {t('success.days')}</div>
            </div>

            {sessionShurikens > 0 && (
              <div>
                <div className="text-gray-600 text-base md:text-2xl">{t('success.shurikens_earned', 'Shurikens earned')}</div>
                <div className="flex items-center justify-center gap-2 text-3xl md:text-5xl font-bold">
                  <img
                    src={sessionShurikens >= 10 ? '/shurikan-gold.png' : '/shurikan-silver.png'}
                    alt="shuriken"
                    className="w-8 h-8 md:w-12 md:h-12 object-contain"
                  />
                  +{sessionShurikens}
                  {newWeeklyShurikens > 0 && (
                    <span className="text-base md:text-2xl text-gray-500 font-normal">
                      ({newWeeklyShurikens} {t('success.this_week', 'this week')})
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {newRank !== null && newRank <= 3 && (
          <div className="mb-6 md:mb-10 bg-yellow-50 border border-yellow-200 rounded-xl p-4 md:p-6 text-center">
            <div className="text-2xl md:text-4xl mb-1">
              {newRank === 1 ? '🥇' : newRank === 2 ? '🥈' : '🥉'}
            </div>
            <div className="font-bold text-yellow-800 text-base md:text-2xl">
              {t('overtake.session_congrats_title')}
            </div>
            <div className="text-yellow-700 text-sm md:text-xl mt-1">
              {t('overtake.session_congrats_body', { rank: newRank })}
            </div>
          </div>
        )}

        {pendingSync && (
          <p className="mb-4 text-sm text-gray-500">{t('success.pending_sync')}</p>
        )}
        <button
          onClick={() => navigate('/home')}
          className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-lg font-bold hover:bg-blue-700 text-xl md:text-4xl min-h-[56px] md:min-h-[100px] flex items-center justify-center"
        >
          {t('common.done')}
        </button>
      </div>
    </div>
  );
}
